import React from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import CurrencyFormat from '../currency-format'
import useStyles from './order-summary-style'
import combineClassNames from '../../helpers/combineClassNames'
import parseLoyaltyPoints from '../../helpers/parse-loyalty-points'
import { useThemeConfig } from '../../hook/use-theme-config'
import useCurrencies from '../../hook/use-currencies'
import OrderService from '../../services/order-service'
import IconPaymentVisa from '../../assets/icons/icon-payment-visa.svg'
import IconPaymentMaster from '../../assets/icons/icon-payment-mastercard.svg'
import IconPaymentApplePay from '../../assets/icons/icon-payment-apple-pay.svg'

const OrderSummaryView = ({
  summary = {},
  paymentRequests = [],
  paymentState,
  className,
  rowClassName,
}) => {
  const { formatCurrency } = useCurrencies()
  const { t } = useTranslation()
  const {
    currencyCode,
    quantityCount,
    deliveryFee,
    serviceCharge,
    vouchers,
    discountGroup,
    totalDiscount,
    subtotal,
    totalLoyaltyPoints,
    total,
  } = summary

  const totalVoucherAmount = _.sumBy(vouchers, ({ totalAmount }) => _.toNumber(totalAmount))

  const style = useStyles()

  const { getConfig } = useThemeConfig()

  const currencyFormatProps = _.omitBy(
    {
      currencyCode,
    },
    _.isUndefined,
  )

  const getVisibility = ({ amount, type }) => {
    const defaultHide = _.includes(
      [
        'deliveryFee',
        'voucher',
        'loyaltyPoints',
        'serviceCharge',
      ],
      type,
    )
    const hideWhenZeroAmount = getConfig(`config.ui.orderSummary.${type}.hideWhenZeroAmount`, defaultHide)
    return (
      !_.isNil(amount)
      && !(
        parseFloat(amount || '0') === 0
        && hideWhenZeroAmount
      )
    )
  }

  const renderPaymentRequestRow = (paymentRequest) => {
    const {
      providerPaymentType,
      providerComputedName,
      // provider,
      paidAmount,
    } = paymentRequest
    const paymentType = OrderService.getPaymentType({ providerPaymentType, providerComputedName })
    const paymentIcons = {
      visa: IconPaymentVisa,
      master: IconPaymentMaster,
      applyPay: IconPaymentApplePay,
    }
    const paymentIconName = _.get(paymentIcons, paymentType)
    const showAmount = paymentState !== 'payment_paid' || _.size(paymentRequests) > 1
    return (
      <div className={combineClassNames([style.row, rowClassName, style.textBlock])}>
        <div
          className={combineClassNames([
            style.paymentRequest,
          ])}
        >
          <span className={combineClassNames([style.value])}>
            {t('screens.orderDetail.summary.payWith', {
              name: providerComputedName,
              context: !_.isEmpty(paymentIconName) ? 'icon' : paymentType,
            })}
          </span>
          {
            !_.isEmpty(paymentIconName) && (
              <img
                alt={paymentType}
                src={paymentIconName}
                className={style.paymentTypeIcon}
              />
            )
          }
          {
            showAmount && (
              <span className={combineClassNames([style.value])}>
                {formatCurrency(paidAmount, { code: currencyCode })}
              </span>
            )
          }
        </div>
      </div>
    )
  }

  return (
    <div className={combineClassNames([style.summary, className])}>
      <div className={combineClassNames([style.row, rowClassName, style.textBlock])}>
        <span>{t('screens.orderDetail.summary.subtotal', { items: quantityCount })}</span>
        <CurrencyFormat {...currencyFormatProps} value={subtotal} />
      </div>
      {/* TODO: need to loop out the discountGroup detail */}
      {
        getVisibility({ amount: totalDiscount, type: 'discount' }) && (
          <div className={combineClassNames([style.row, rowClassName])}>
            <div className={style.textBlock}>
              <span>{t('screens.orderDetail.summary.discount')}</span>
              <CurrencyFormat {...currencyFormatProps} value={totalDiscount} />
            </div>
            {
              !_.isEmpty(discountGroup) && (
                <div className={style.infoBlock}>
                  {
                    discountGroup.map((discount) => (
                      <div key={`discount-group-${discount.uuid}`} className={style.info}>
                        <span>{discount.title}</span>
                        <CurrencyFormat {...currencyFormatProps} value={discount.totalAmount} />
                      </div>
                    ))
                  }
                </div>
              )
            }
          </div>
        )
      }
      {
        getVisibility({ amount: totalVoucherAmount, type: 'voucher' }) && (
          <div className={combineClassNames([style.row, rowClassName])}>
            <div className={style.textBlock}>
              <span>{t('screens.orderDetail.summary.vouchers')}</span>
              <CurrencyFormat {...currencyFormatProps} value={totalVoucherAmount} />
            </div>
            <div className={style.infoBlock}>
              {
                _.map(vouchers, (amount) => (
                  getVisibility({ amount, type: 'vouchers' }) && (
                    <div key={`voucher-amount-${amount.uuid}`} className={style.info}>
                      <span>{amount.title}</span>
                      <CurrencyFormat {...currencyFormatProps} value={amount.totalAmount} />
                    </div>
                  )
                ))
              }
            </div>
          </div>
        )
      }
      {
        getVisibility({ amount: totalLoyaltyPoints, type: 'loyaltyPoints' }) && (
          <div className={combineClassNames([style.row, rowClassName, style.textBlock])}>
            <span>{t('screens.orderDetail.summary.loyaltyPoints')}</span>
            {
              parseFloat(totalLoyaltyPoints || '0') > 0 ? (
                <span>{parseLoyaltyPoints(totalLoyaltyPoints)}</span>
              ) : (
                <span>--</span>
              )
            }
          </div>
        )
      }
      { getVisibility({ amount: deliveryFee, type: 'deliveryFee' }) && (
        <div className={combineClassNames([style.row, rowClassName, style.textBlock])}>
          <span>{t('screens.orderDetail.summary.delivery')}</span>
          {
            parseFloat(deliveryFee || '0') > 0 ? (
              <CurrencyFormat {...currencyFormatProps} value={deliveryFee} />
            ) : (
              <span>--</span>
            )
          }
        </div>
      )}
      { getVisibility({ amount: serviceCharge, type: 'serviceCharge' }) && (
        <div className={combineClassNames([style.row, rowClassName, style.textBlock])}>
          <span>{t('screens.orderDetail.summary.service')}</span>
          {
            parseFloat(serviceCharge || '0') > 0 ? (
              <CurrencyFormat {...currencyFormatProps} value={serviceCharge} />
            ) : (
              <span>--</span>
            )
          }
        </div>
      )}
      { getVisibility({ amount: total, type: 'total' }) && (
        <div className={combineClassNames([style.row, rowClassName, style.textBlock, style.total])}>
          <span>{t('screens.orderDetail.summary.total')}</span>
          <CurrencyFormat {...currencyFormatProps} value={total} />
        </div>
      )}
      {_.map(_.filter(paymentRequests, ['state', 'completed']), (paymentRequest) => (
        <React.Fragment key={paymentRequest.id}>
          {renderPaymentRequestRow(paymentRequest)}
        </React.Fragment>
      ))}
    </div>
  )
}

export default OrderSummaryView
